import { useState, useMemo } from "react";
import { Task } from "../types/task";
import { FormFilters } from "../types/formFilters";

interface UseTaskFiltersProps {
  tasks: Task[];
}

const normalizeDate = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const useTaskFilters = ({ tasks }: UseTaskFiltersProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<FormFilters>({
    taskId: { from: null, to: null },
    product: undefined,
    user: undefined,
    procedureType: undefined,
    procedureId: undefined,
    creationDate: null,
    editionDate: null,
    validation: [],
    criteria: [],
    status: [],
    taskValidation: []
  });

  const visibleTasks = tasks.filter((task) => task.deleted_at === null);
  
  const filteredTasks = useMemo(() => {
    let filtered = visibleTasks;

    if (searchTerm.trim()) {
      filtered = filtered.filter((task) =>
        task.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (appliedFilters.criteria.length > 0) {
      filtered = filtered.filter((task) =>
        appliedFilters.criteria.some(criteria =>
          task.criteria?.includes(criteria)
        )
      );
    }

    if (appliedFilters.creationDate) {
      const [startDate, endDate] = appliedFilters.creationDate;
      if (startDate && endDate) {
        filtered = filtered.filter((task) => {
          const taskDateOnly = normalizeDate(new Date(task.created_at));
          const startDateOnly = normalizeDate(startDate.toDate());
          const endDateOnly = normalizeDate(endDate.toDate());
          
          return taskDateOnly >= startDateOnly && taskDateOnly <= endDateOnly;
        });
      }
    }

    if (appliedFilters.status.length > 0) {
      filtered = filtered.filter((task) =>
        appliedFilters.status.includes(task.status)
      );
    }

    if (appliedFilters.taskValidation.length > 0) {
      filtered = filtered.filter((task) => {
        const isValidated = task.validated_at !== null;
        return appliedFilters.taskValidation.some(validation => {
          if (validation === 'validated') return isValidated;
          if (validation === 'pending') return !isValidated;
          return false;
        });
      });
    }

    return filtered;
  }, [visibleTasks, searchTerm, appliedFilters]);

  const handleApplyFilters = (filters: FormFilters) => {
    setAppliedFilters(filters);
  };

  const handleRemoveFilter = (filterType: keyof FormFilters, value?: string) => {
    setAppliedFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterType === 'criteria' && value) {
        newFilters.criteria = newFilters.criteria.filter(c => c !== value);
      } else if (filterType === 'status' && value) {
        newFilters.status = newFilters.status.filter(s => s !== value);
      } else if (filterType === 'taskValidation' && value) {
        newFilters.taskValidation = newFilters.taskValidation.filter(v => v !== value);
      } else if (filterType === 'creationDate') {
        newFilters.creationDate = null;
      }
      
      return newFilters;
    });
  };

  const handleClearAllFilters = () => {
    setAppliedFilters({
      taskId: { from: null, to: null },
      product: undefined,
      user: undefined,
      procedureType: undefined,
      procedureId: undefined,
      creationDate: null,
      editionDate: null,
      validation: [],
      criteria: [],
      status: [],
      taskValidation: []
    });
  };

  return {
    searchTerm,
    setSearchTerm,
    appliedFilters,
    filteredTasks,
    handleApplyFilters,
    handleRemoveFilter,
    handleClearAllFilters
  };
};
